import StackKit from "@/shared/components/kit/stack";
import { useState } from "react";
import TableWallet from "../../walletReports/table";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import { useQuery } from "@tanstack/react-query";
import { fetchCreditPurchases } from "@/api/user/home";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import moment from "jalali-moment";
import { useNavigate } from "react-router";
import ContentSkeleton from "../../walletReports/content.skeleton";

/**
 * Fixed Content Component
 *
 * کامپوننت دریافت و نمایش لیست خریدهای اعتباری با جلوگیری کامل از خطاهای map
 */

const Content = () => {
  const [loanID] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();
  
  const { data, isLoading, isError } = useQuery({
    queryKey: ["storePoint-data", loanID, currentPage],
    queryFn: () => fetchCreditPurchases(loanID, currentPage),
  });

  // Extract pagination data from API response
  const paginationData = data?.data ? {
    current_page: data.data.current_page || currentPage,
    per_page: data.data.per_page || itemsPerPage,
    total: data.data.total || 0,
  } : undefined;

  const dataHeader = [
    { id: "totalPayed", title: "خرید اعتباری" },
    { id: "installmentsCount", title: "کل اقساط" },
    { id: "payedCount", title: "اقساط پرداخت شده" },
    { id: "sumPayment", title: "مبلغ هر قسط" },
    { id: "clearingAmount", title: "مبلغ تسویه یکجا" },
    { id: "start", title: "تاریخ دریافت اعتبار" },
    { id: "end", title: "تاریخ پایان اعتبار" },
    { id: "details", title: "پذیرنده دریافت کننده اعتبار" },
    { id: "detailButton", title: "" },
    { id: "moreElement", title: "" },
  ];

  // --- Handle loading state first ---
  if (isLoading) {
    return (
      <CardBoxLayout>
        <ContentSkeleton />
      </CardBoxLayout>
    );
  }

  // --- Handle error state (optional) ---
  if (isError) {
    return (
      <CardBoxLayout>
        <StackKit justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <p>خطا در دریافت اطلاعات</p>
        </StackKit>
      </CardBoxLayout>
    );
  }

  // --- SAFE DATA EXTRACTION (after loading check) ---
  // Check both old and new API response structures for backward compatibility
  const list = Array.isArray(data?.data?.loans) 
    ? data.data.loans  // New structure
    : Array.isArray(data?.data) 
      ? data.data      // Old structure (for backward compatibility)
      : [];

  const newDataStorePont = list.map((item: any, index: number) => {
    const installments = Array.isArray(item?.data?.installments)
      ? item.data.installments
      : [];

    return {
      rowId: toPersianNumber((paginationData?.current_page - 1) * (paginationData?.per_page || 10) + index + 1),
      ...item,

      totalPayed: numberSeparator(`${item?.data?.totalPayed ?? 0}`),
      installmentsCount: toPersianNumber(item?.data?.installmentsCount ?? 0),
      payedCount: toPersianNumber(item?.data?.payedCount ?? 0),

      sumPayment: numberSeparator(`${installments[0]?.sum ?? 0}`),
      clearingAmount: numberSeparator(item?.data?.clearingAmount ?? 0),

      start: toPersianNumber(
        moment.unix(item?.start ?? 0).locale("fa").format("YYYY-MM-DD")
      ),

      end: toPersianNumber(
        moment.unix(item?.end ?? 0).locale("fa").format("YYYY-MM-DD")
      ),

      details: item?.details ?? "",

      detailButton: (
        <StackKit
          justifyContent="center"
          alignItems="center"
          sx={{
            background:
              "linear-gradient(147.72deg, #272727 -17.1%, #333333 118.66%)",
            borderRadius: "50%",
            height: "30px",
            width: "30px",
            cursor: "pointer",
          }}
          onClick={() => navigate(`/credits/${item?.id}`)}
        >
          <NorthWestIcon style={{ color: "#fff", width: "15px" }} />
        </StackKit>
      ),
    };
  });

  // Handle empty data state
  if (list.length === 0) {
    return (
      <CardBoxLayout>
        <StackKit justifyContent="center" alignItems="center" sx={{ py: 4 }}>
          <p>اطلاعاتی برای نمایش وجود ندارد</p>
        </StackKit>
      </CardBoxLayout>
    );
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Optional: Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    // Note: You might need to update the API call to support per_page parameter
    // If your API doesn't support it, this will only affect the TableWallet display
  };

  return (
    <CardBoxLayout>
      <StackKit alignItems="start" justifyContent="start">
        <TableWallet 
          dataHeader={dataHeader} 
          dataTbody={newDataStorePont}
          pagination={paginationData}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </StackKit>
    </CardBoxLayout>
  );
};

export default Content;